import React from 'react';
import { toast } from 'react-toastify';
import Api from '@plone/volto/helpers/Api/Api';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import { tiposConfigurados } from './config';

/**
 * Auditoria de acessibilidade automática no salvamento.
 *
 * O add-on v2inclusiva.a11y audita a notícia dentro do próprio salvamento (evento
 * padrão do Plone) e grava o veredito nos campos a11y_* do conteúdo. Estes
 * helpers só LEEM esse veredito para mostrar ao editor.
 */

// Mesmos cortes do auditor (nota_aprovacao / nota_minima). Só é usado quando o
// conteúdo foi auditado antes de existir o campo a11y_faixa.
const NOTA_APROVADO = 95;
const NOTA_RESSALVA = 85;

// Campos de vocabulário (Choice) chegam como {token, title}; os demais, como string.
const token = (v) => (v && typeof v === 'object' ? v.token : v) || null;

/**
 * Normaliza os campos a11y_* de um conteúdo salvo. Devolve null se o conteúdo
 * nunca foi auditado (sem nota).
 */
export function lerResultadoSalvo(content) {
  if (!content || typeof content.a11y_nota !== 'number') return null;
  let issues = [];
  try {
    const parsed = JSON.parse(content.a11y_erros || '[]');
    if (Array.isArray(parsed)) issues = parsed;
  } catch (e) {
    issues = [];
  }
  const score = content.a11y_nota;
  let faixa = token(content.a11y_faixa);
  if (!faixa) {
    if (score >= NOTA_APROVADO) faixa = 'aprovado';
    else if (score >= NOTA_RESSALVA) faixa = 'com_ressalva';
    else faixa = 'bloqueado';
  }
  return {
    score,
    faixa,
    issues,
    total_issues: issues.length,
    can_publish: content.a11y_bloqueado !== true,
    a11y_prazo: content.a11y_prazo || null,
    a11y_status: token(content.a11y_status),
    ultima_auditoria: content.a11y_ultima_auditoria || null,
    saved: true,
  };
}

/**
 * Avisa o editor do resultado da auditoria automática (toast). Não faz nada para
 * tipos fora do escopo ou conteúdo sem veredito (auditor indisponível, recurso
 * desligado) e nunca lança erro: é só informativo.
 */
export function avisarResultadoAuditoria(content) {
  const r = lerResultadoSalvo(content);
  if (!r) return;
  // Se a configuração já foi carregada, só avisa para tipos verificados; se
  // ainda não, confia no veredito gravado pelo backend (a própria presença da
  // nota já indica que o tipo é auditado).
  const config = tiposConfigurados();
  if (config && !config.some((t) => t.id === content['@type'])) return;
  const nota = r.score.toFixed(1).replace('.', ',');
  const falhas = r.total_issues;
  if (r.faixa === 'aprovado') {
    toast.success(
      <Toast
        success
        title="Acessibilidade verificada"
        content={`Nota ${nota}. Conteúdo conforme.`}
      />,
    );
    return;
  }
  const prazo = r.a11y_prazo
    ? ` Prazo de correção: ${new Date(r.a11y_prazo).toLocaleDateString('pt-BR')}.`
    : '';
  const msg = `Nota ${nota} com ${falhas} falha${falhas === 1 ? '' : 's'}.${prazo}`;
  if (r.can_publish) {
    toast.warn(
      <Toast warning title="Acessibilidade com ressalvas" content={msg} />,
    );
  } else {
    toast.error(
      <Toast
        error
        title="Acessibilidade: publicação bloqueada"
        content={msg}
      />,
    );
  }
}

/**
 * Após salvar na EDIÇÃO: o PATCH não devolve o conteúdo, então busca o salvo e
 * avisa o resultado.
 */
export async function avisarResultadoAposEdicao(path) {
  try {
    const content = await new Api().get(path);
    avisarResultadoAuditoria(content);
  } catch (e) {
    // informativo: falha aqui não deve atrapalhar o fluxo de salvar
  }
}
